import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Divider,
  Button,
  Box,
  TextField,
  Grid,
  Tabs,
  Tab,
  Alert,
  InputAdornment,
  Paper,
  Stack,
} from "@mui/material";
import {
  useGetTenantInformationQuery,
  useUpdateTenantMutation,
  useUpdateTenantLogoMutation,
  useUpdateTenantTestingSettingsMutation,
} from "../../../features/tenants/tenantsApiSlice";
import { uploadTenantLogoAxios } from "../../../app/api/axios/services/files.service";
import { useDispatch, useSelector } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";
import { serverURL } from "../../../app/api/axios/http.service";
import { selectCurrentUserData } from "../../../features/auth/authSlice";
import UploadTenantLogoField from "./UploadTenantLogoField";
import Loading from "../../../components/spinners/Loading";
import { gradArray } from "../../../components/form/fields/inputFields";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

export default function TenantSettings() {
  const [logo, setLogo] = useState(null);
  const [logoFeedback, setLogoFeedback] = useState(null);
  const [isSavingLogo, setIsSavingLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(() => Date.now());
  const [tenantForm, setTenantForm] = useState({
    tenantName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    mail: "",
    homepage: "",
    taxNumber: "",
  });
  const [testingFees, setTestingFees] = useState({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [updateTenantLogo] = useUpdateTenantLogoMutation();
  const [updateTenant] = useUpdateTenantMutation();
  const [updateTenantTestingSettings] =
    useUpdateTenantTestingSettingsMutation();
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const currentUserData = useSelector(selectCurrentUserData);
  const dispatch = useDispatch();
  const location = useLocation();

  const degreeOptions = useMemo(
    () => gradArray.filter((grad) => grad.id !== 1),
    [],
  );

  useEffect(() => {
    if (!tenantInfo) return;
    setTenantForm({
      tenantName: tenantInfo?.tenantName || "",
      address: tenantInfo?.address || "",
      zip: tenantInfo?.zip || "",
      city: tenantInfo?.city || "",
      phone: tenantInfo?.phone || "",
      mail: tenantInfo?.mail || "",
      homepage: tenantInfo?.homepage || "",
      taxNumber: tenantInfo?.taxNumber || "",
    });
    const tenantFees = tenantInfo?.settings?.testingSettings?.fees || {};
    const nextFees = {};
    degreeOptions.forEach((grad) => {
      const key = String(grad.id);
      const value = tenantFees?.[key];
      nextFees[key] = Number.isFinite(value) ? value : value ?? "";
    });
    setTestingFees(nextFees);
  }, [tenantInfo, degreeOptions]);

  const handleFieldChange = (field) => (event) => {
    setTenantForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSaveSettings = async () => {
    setShowValidationErrors(true);
    const normalizedTaxNumber = tenantForm.taxNumber?.trim() || "";
    if (!normalizedTaxNumber) {
      dispatch(
        setDialog({
          title: "Steuernummer erforderlich",
          type: "ERROR",
          data: {
            errorText:
              "Bitte hinterlegen Sie eine Steuernummer in den Stammdaten.",
          },
        }),
      );
      return;
    }

    try {
      const result = await updateTenant({
        tenantName: tenantForm.tenantName?.trim() || "",
        address: tenantForm.address?.trim() || "",
        zip: tenantForm.zip?.trim() || "",
        city: tenantForm.city?.trim() || "",
        phone: tenantForm.phone?.trim() || "",
        mail: tenantForm.mail?.trim() || "",
        homepage: tenantForm.homepage?.trim() || "",
        taxNumber: normalizedTaxNumber,
      }).unwrap();

      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "Einstellungen gespeichert!",
            type: "SUCCESS",
            reload: true,
            data: {
              successText: "Die Einstellungen wurden erfolgreich aktualisiert.",
            },
          }),
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
    }
  };

  const handleTestingFeeChange = (key) => (event) => {
    setTestingFees((prev) => ({
      ...prev,
      [key]: event.target.value,
    }));
  };

  const handleSaveTestingSettings = async () => {
    try {
      const sanitizedFees = {};
      Object.entries(testingFees).forEach(([key, value]) => {
        const parsedValue = Number(value);
        sanitizedFees[key] = Number.isFinite(parsedValue) ? parsedValue : 0;
      });
      const result = await updateTenantTestingSettings(sanitizedFees).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "Prüfungsgebühren gespeichert!",
            type: "SUCCESS",
            reload: true,
            data: {
              successText:
                "Die Prüfungsgebühren wurden erfolgreich aktualisiert.",
            },
          }),
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
    }
  };

  const handleChangeTenantLogo = async () => {
    if (!logo) {
      setLogoFeedback({
        type: "error",
        message: "Bitte wählen Sie zuerst ein Logo aus.",
      });
      return;
    }

    setLogoFeedback(null);
    setIsSavingLogo(true);

    try {
      const result = await updateTenantLogo({
        logoName: logo.name,
      }).unwrap();

      if (!result || result.type !== "success") {
        throw new Error("Das Logo konnte nicht aktualisiert werden.");
      }

      const formData = new FormData();
      formData.append("file", logo);
      const uploadResult = await uploadTenantLogoAxios(formData);

      if (uploadResult?.data?.status === "success") {
        const nextLogoVersion = Date.now();
        setLogoVersion(nextLogoVersion);
        window.localStorage.setItem(
          "tenant_logo_version",
          String(nextLogoVersion),
        );
        window.dispatchEvent(
          new CustomEvent("tenant-logo-updated", {
            detail: { version: nextLogoVersion },
          }),
        );
        setLogoFeedback({
          type: "success",
          message: "Das Logo wurde erfolgreich hochgeladen.",
        });
        setLogo(null);
      } else {
        throw new Error("Beim Speichern der Datei ist ein Fehler aufgetreten.");
      }
    } catch (error) {
      const errorMessage =
        error?.data?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Beim Hochladen des Logos ist ein Fehler aufgetreten.";
      setLogoFeedback({ type: "error", message: errorMessage });
    } finally {
      setIsSavingLogo(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  const basePath = "/dashboard/settings/tenant";
  const tenantTabs = [
    { label: "Stammdaten", path: `${basePath}/general` },
    { label: "Logo", path: `${basePath}/logo` },
    { label: "Prüfungsgebühren", path: `${basePath}/testing` },
  ];
  const activeTab =
    tenantTabs.find((tab) => location.pathname.startsWith(tab.path))?.path ||
    tenantTabs[0].path;

  return (
    <>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Unternehmen
      </Typography>
      <Tabs
        value={activeTab}
        sx={{ mb: 3 }}
        textColor="primary"
        indicatorColor="primary"
      >
        {tenantTabs.map((tab) => (
          <Tab
            key={tab.path}
            label={tab.label}
            value={tab.path}
            component={NavLink}
            to={tab.path}
          />
        ))}
      </Tabs>
      <Divider sx={{ mb: 3 }} />
      <Routes>
        <Route index element={<Navigate to="general" replace />} />
        <Route
          path="general"
          element={
            <TenantGeneralSettings
              tenantForm={tenantForm}
              onChange={handleFieldChange}
              onSave={handleSaveSettings}
              showValidationErrors={showValidationErrors}
            />
          }
        />
        <Route
          path="logo"
          element={
            <TenantLogoSettings
              currentTenantId={currentUserData?.tenant}
              logo={logo}
              logoVersion={logoVersion}
              logoFeedback={logoFeedback}
              isSavingLogo={isSavingLogo}
              onLogoChange={setLogo}
              onSaveLogo={handleChangeTenantLogo}
            />
          }
        />
        <Route
          path="testing"
          element={
            <TenantTestingSettings
              degreeOptions={degreeOptions}
              testingFees={testingFees}
              onChangeFee={handleTestingFeeChange}
              onSave={handleSaveTestingSettings}
            />
          }
        />
      </Routes>
    </>
  );
}

function TenantGeneralSettings({
  tenantForm,
  onChange,
  onSave,
  showValidationErrors,
}) {
  return (
    <>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Stammdaten
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Diese Angaben werden u. a. in der Mitgliederanmeldung und in
        Benachrichtigungs-PDFs (z. B. bei neuen Mitgliedern) verwendet.
      </Alert>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name"
            variant="outlined"
            value={tenantForm.tenantName}
            onChange={onChange("tenantName")}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Adresse"
            variant="outlined"
            value={tenantForm.address}
            onChange={onChange("address")}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="PLZ"
            variant="outlined"
            value={tenantForm.zip}
            onChange={onChange("zip")}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Stadt"
            variant="outlined"
            value={tenantForm.city}
            onChange={onChange("city")}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Telefon"
            variant="outlined"
            value={tenantForm.phone}
            onChange={onChange("phone")}
            type="tel"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="E-Mail"
            variant="outlined"
            value={tenantForm.mail}
            onChange={onChange("mail")}
            type="email"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Website"
            variant="outlined"
            value={tenantForm.homepage}
            onChange={onChange("homepage")}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Steuernummer"
            variant="outlined"
            value={tenantForm.taxNumber}
            onChange={onChange("taxNumber")}
            fullWidth
            required
            error={showValidationErrors && !tenantForm.taxNumber?.trim()}
            helperText="Wird u. a. auf Shop-Rechnungen verwendet."
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mb: 3 }}>
        <Button variant="contained" color="secondary" onClick={onSave}>
          Einstellungen speichern
        </Button>
      </Box>
    </>
  );
}

function TenantLogoSettings({
  currentTenantId,
  logo,
  logoVersion,
  logoFeedback,
  isSavingLogo,
  onLogoChange,
  onSaveLogo,
}) {
  const [logoLoadError, setLogoLoadError] = useState(false);

  useEffect(() => {
    setLogoLoadError(false);
  }, [logoVersion]);

  return (
    <>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Logo verwalten
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Das Logo wird an mehreren Stellen verwendet (z. B. öffentliche
        Anmeldung, PDFs und E-Mails).
      </Typography>

      {logoFeedback ? (
        <Alert severity={logoFeedback.type} sx={{ mb: 3 }}>
          {logoFeedback.message}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
              Aktuelles Logo
            </Typography>
            {logoLoadError ? (
              <Alert severity="info">
                Es ist aktuell kein Logo hinterlegt oder das Bild konnte nicht
                geladen werden.
              </Alert>
            ) : (
              <Box
                component="img"
                src={`${serverURL()}/public/tenant/image/${
                  currentTenantId ?? " "
                }?v=${logoVersion}`}
                alt="Aktuelles Tenant-Logo"
                onError={() => setLogoLoadError(true)}
                sx={{
                  width: "100%",
                  maxHeight: 180,
                  objectFit: "contain",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  bgcolor: "background.default",
                }}
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Neues Logo hochladen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Empfohlen ist ein breites Logo (z. B. 120 x 60 px). Nach der
                Auswahl speichern Sie mit einem Klick auf den Button.
              </Typography>
              <UploadTenantLogoField logo={logo} setLogo={onLogoChange} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          sx={{ minWidth: 250 }}
          variant="contained"
          color="primary"
          disabled={!logo || isSavingLogo}
          onClick={onSaveLogo}
        >
          {isSavingLogo ? "Wird hochgeladen..." : "Logo hochladen"}
        </Button>
      </Box>
    </>
  );
}

function TenantTestingSettings({
  degreeOptions,
  testingFees,
  onChangeFee,
  onSave,
}) {
  return (
    <>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Prüfungsgebühren
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Die Gebühr bezieht sich auf den nächsten Gürtelgrad (z. B. 5. Kup ➜ 4.
        Kup). Für 10. Kup gibt es keine Prüfung. Alle Beträge sind in Euro (€).
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {degreeOptions.map((grad) => {
          const key = String(grad.id);
          return (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <TextField
                label={grad.label}
                variant="outlined"
                value={testingFees?.[key] ?? ""}
                onChange={onChangeFee(key)}
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">€</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
              />
            </Grid>
          );
        })}
      </Grid>
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" color="secondary" onClick={onSave}>
          Gebühren speichern
        </Button>
      </Box>
    </>
  );
}
